import React from 'react';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core';

import { useMediaQuery } from 'ha/helpers/MediaQuery';
import units from 'ha/theme/units';

import Grid from 'ha/ui/next/Grid';
import { DeviceType } from 'ha/components/Filters/DateFilter/DateFilter';
import { WidthType } from 'ha/components/Filters/FilterPopover';
import { useIntl } from 'ha/i18n';
import DateFilterButton from '../containers/SearchFilters/DateFilterButton';

import SearchLocationTitle from '../containers/SearchLocationTitle';
import SearchAdvertiserTitle from '../containers/SearchAdvertiserTitle';

import DateFilter from '../containers/SearchFilters/DateFilter';
import OtherFilterDesktop from '../containers/SearchFilters/OtherFilterDesktop';
import OtherFilterMobile from '../containers/SearchFilters/OtherFilterMobile';
import PriceFilterDesktop from '../containers/SearchFilters/PriceFilterDesktop';
import SearchAlertDesktop from '../containers/SearchAlerts/SearchAlertDesktop';
import SortingButton from './SortingButton';

import SearchAlertMobile from '../containers/SearchAlerts/SearchAlertMobileButton';
import ToggleMobileMap from '../containers/ToggleMobileMap';
import PriceFilterButton from '../containers/SearchFilters/PriceFilterButton';
import OtherFilterButton from '../containers/SearchFilters/OtherFilterButton';
import { filterUpdateButtonText, DefaultTextType } from '../utils';

const useStyles = makeStyles(theme => ({
  searchLocationTitle: {
    margin: units.trbl(0, 1.5, 0.5, 1.5),
  },
  searchLocationTitleMobile: {
    margin: units.trbl(0, 0.7, 1, 0.7),
  },
  buttonContainer: {
    flexShrink: 0,
    marginRight: units.rem(0.5),
  },
  flexStill: {
    display: 'flex',
    flexGrow: 0,
    flexShrink: 0,
  },
  buttonFilterContainer: {
    display: 'flex',
    overflowX: 'auto',
    flexGrow: 1,
  },
  controlsContainer: {
    padding: units.yx(1, 0.7),
    [theme.breakpoints.up('lg')]: {
      padding: units.yx(1, 1.5),
    },
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.white.main,
    zIndex: 3,
  },
  title: {
    [theme.breakpoints.down('md')]: {
      marginTop: units.rem(1),
    },
  },
}));

interface Props {
  previewNumberOfListings?: number;
  isAdvertiserSearch: boolean;
  isSearchEmpty: boolean;
}

export const SearchOverview: React.FC<Props> = ({
  previewNumberOfListings,
  isAdvertiserSearch,
  isSearchEmpty,
}) => {
  const { T } = useIntl();
  const classes = useStyles();
  const { lg } = useMediaQuery();

  return (
    <React.Fragment>
      <div className={classes.controlsContainer}>
        <Grid container>
          <div
            className={classes.buttonFilterContainer}
            data-test-locator="Search Overview Filters"
          >
            <div className={classes.buttonContainer}>
              <SortingButton size={lg ? 'medium' : 'small'} />
            </div>
            <div className={classes.buttonContainer}>
              <DateFilter
                button={<DateFilterButton size={lg ? 'medium' : 'small'} />}
                type={lg ? DeviceType.Desktop : DeviceType.Mobile}
                updateButtonText={filterUpdateButtonText(
                  T,
                  DefaultTextType.Update,
                  previewNumberOfListings,
                )}
              />
            </div>
            <div className={classes.buttonContainer}>
              {lg ? (
                <OtherFilterDesktop
                  button={<OtherFilterButton size="medium" />}
                  updateButtonText={filterUpdateButtonText(
                    T,
                    DefaultTextType.Update,
                    previewNumberOfListings,
                  )}
                />
              ) : (
                <OtherFilterMobile size="small" />
              )}
            </div>

            {lg && (
              <div className={classes.buttonContainer}>
                <PriceFilterDesktop
                  width={WidthType.lg}
                  priceFilterTitle={T('Budget')}
                  button={<PriceFilterButton />}
                  updateButtonText={filterUpdateButtonText(
                    T,
                    DefaultTextType.Update,
                    previewNumberOfListings,
                  )}
                />
              </div>
            )}
          </div>

          {!isAdvertiserSearch && lg && !isSearchEmpty && (
            <div className={classes.flexStill}>
              <SearchAlertDesktop />
            </div>
          )}
        </Grid>
      </div>
      <div
        className={cx({
          [classes.searchLocationTitle]: lg,
          [classes.searchLocationTitleMobile]: !lg,
        })}
      >
        {!lg && (
          <Grid container>
            <div className={classes.buttonFilterContainer}>
              <div className={classes.buttonContainer}>
                <ToggleMobileMap color="secondary" />
              </div>
              {!isAdvertiserSearch && !isSearchEmpty && (
                <div className={classes.buttonContainer}>
                  <SearchAlertMobile />
                </div>
              )}
            </div>
          </Grid>
        )}
        {isAdvertiserSearch ? (
          <SearchAdvertiserTitle />
        ) : (
          <Grid
            container
            justify="space-between"
            alignItems="flex-start"
            wrap={lg ? 'nowrap' : 'wrap'}
          >
            <Grid item zeroMinWidth className={classes.title}>
              <SearchLocationTitle />
            </Grid>
          </Grid>
        )}
      </div>
    </React.Fragment>
  );
};

export default SearchOverview;
